// src/components/DocumentationDownload/SubscriptionInvoicePdfDownLoad.jsx
import React, { useMemo, useRef, useState } from "react";
import jsPDF from "jspdf";
import "jspdf-autotable";
import html2canvas from "html2canvas";
import "./subscriptionInvoicePdfDownLoad.css";

// When nothing changed, allow at most this many downloads.
const MAX_SAME_DOWNLOADS = 3;

/** ---------- utilities: formatting ---------- */
function formatMoney(v, ccy = "USD") {
  const n = Number(v);
  if (!Number.isFinite(n)) return "-";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: ccy }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
}
function formatDate(isoLike) {
  if (!isoLike) return "-";
  const d = new Date(isoLike);
  return Number.isNaN(d.getTime())
    ? "-"
    : d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}
function maskEmail(email) {
  if (!email || typeof email !== "string") return "-";
  const [u, d] = email.split("@");
  if (!d) return email;
  const left = u.length <= 2 ? u[0] : `${u[0]}${"*".repeat(Math.min(u.length - 2, 6))}${u.slice(-1)}`;
  return `${left}@${d}`;
}
const mkId = (prefix) =>
  `${prefix || "req"}_${(crypto?.randomUUID?.() || Math.random().toString(36).slice(2)).replace(/-/g, "")}`;
const computeChangeHash = (payload) => {
  const s = [
    payload?.invoiceId || "",
    payload?.id || "",
    payload?.planName || "",
    payload?.price ?? "",
    payload?.currency || "",
    payload?.nextBilling || "",
    payload?.payerEmail || "",
  ].join("|");
  try {
    return btoa(unescape(encodeURIComponent(s))).slice(0, 22);
  } catch {
    return `chg_${s.length}`;
  }
};

/** ---------- guardrails: idempotency + retries + publisher ---------- */

/** Simple in-memory idempotency store with TTL (ms). */
class IdempotencyStore {
  constructor({ ttlMs = 30 * 60 * 1000 } = {}) {
    this.ttlMs = ttlMs;
    this.map = new Map(); // key -> { status: 'in_progress'|'complete', expiresAt, changeHash }
  }
  tryStart(key, changeHash) {
    const now = Date.now();
    const entry = this.map.get(key);
    // Only block concurrent in-flight work; completed items are allowed again (quota handled elsewhere).
    if (entry && entry.expiresAt > now && entry.status === "in_progress") return false;
    this.map.set(key, { status: "in_progress", changeHash, expiresAt: now + this.ttlMs });
    return true;
  }
  markComplete(key, changeHash) {
    const now = Date.now();
    this.map.set(key, { status: "complete", changeHash, expiresAt: now + this.ttlMs });
  }
  forget(key) {
    this.map.delete(key);
  }
}

/** Exponential backoff with jitter and attempt cap. */
async function withBackoff(fn, { attempts = 4, baseMs = 300, factor = 2, jitter = 0.25 } = {}) {
  let lastErr;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn(i + 1);
    } catch (err) {
      lastErr = err;
      if (err?.permanent || i === attempts - 1) break;
      const exp = baseMs * Math.pow(factor, i);
      const rand = 1 + (Math.random() * 2 - 1) * jitter;
      const delay = Math.max(50, Math.floor(exp * rand));
      await new Promise((r) => setTimeout(r, delay));
    }
  }
  throw lastErr;
}

/** Minimal event publisher with allow-list routing + loop/echo prevention and hop limit. */
class EventPublisher {
  constructor({ service, allowedRoutes = {}, maxHopCount = 9 } = {}) {
    this.service = service;
    this.allowedRoutes = allowedRoutes;
    this.maxHopCount = Math.max(1, Math.min(16, maxHopCount));
    this.dlq = [];
  }
  publish(envelope, { ingress, egress }) {
    if (!ingress || !egress) return { dropped: true, reason: "invalid_route" };
    const allowed = this.allowedRoutes[ingress] || [];
    if (!allowed.includes(egress)) return { dropped: true, reason: "egress_not_allowed" };
    if (envelope.source === this.service) return { dropped: true, reason: "self_source" };
    if (Array.isArray(envelope.visited) && envelope.visited.includes(this.service)) {
      return { dropped: true, reason: "seen" };
    }
    const hopCount = Number(envelope.hopCount || 0) + 1;
    if (hopCount > this.maxHopCount) {
      this.dlq.push({ envelope, reason: "hop_limit", at: new Date().toISOString() });
      return { dropped: true, reason: "hop_limit" };
    }
    const forwarded = {
      ...envelope,
      visited: [...(envelope.visited || []), this.service],
      hopCount,
      publishedAt: new Date().toISOString(),
    };
    // eslint-disable-next-line no-console
    console.info(`[${this.service}] publish`, { ingress, egress, forwarded });
    return { ok: true, forwarded };
  }
}

/** ---------- PDF helpers ---------- */

function pickPdfSize() {
  if (typeof window !== "undefined" && window.matchMedia?.("(max-width: 480px)").matches) return "a5";
  return "a4";
}
function computeCaptureScale() {
  const dpr = (typeof window !== "undefined" && window.devicePixelRatio) || 1;
  const isSmall = typeof window !== "undefined" && window.matchMedia?.("(max-width: 480px)").matches;
  const base = isSmall ? 2.5 : 2.0;
  return Math.min(4, base * dpr * 0.9);
}
async function canvasToPdf(pdf, canvas, { marginMm = 10 } = {}) {
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const usableW = pageW - marginMm * 2;
  const usableH = pageH - marginMm * 2;

  const ratioMmPerPx = usableW / canvas.width;
  const sliceHeightPx = Math.floor(usableH / ratioMmPerPx);

  let yPx = 0;
  let pageIndex = 0;

  while (yPx < canvas.height) {
    const sliceCanvas = document.createElement("canvas");
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = Math.min(sliceHeightPx, canvas.height - yPx);

    const ctx = sliceCanvas.getContext("2d");
    ctx.drawImage(canvas, 0, yPx, canvas.width, sliceCanvas.height, 0, 0, sliceCanvas.width, sliceCanvas.height);

    const imgData = sliceCanvas.toDataURL("image/png");
    if (pageIndex > 0) pdf.addPage();
    pdf.addImage(imgData, "PNG", marginMm, marginMm, usableW, sliceCanvas.height * ratioMmPerPx);

    yPx += sliceHeightPx;
    pageIndex += 1;
  }
}

/** Persist/Read last successful download record (per idempotencyKey), including a counter. */
function persistLastDownloadInfo(key, changeHash) {
  try {
    const k = `phn_invoice_last_hash_${key}`;
    const raw = localStorage.getItem(k);
    let count = 1;
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.changeHash === changeHash) {
        count = Math.min(MAX_SAME_DOWNLOADS, Number(parsed?.count || 0) + 1);
      }
    }
    localStorage.setItem(k, JSON.stringify({ changeHash, count, at: Date.now() }));
  } catch {}
}
function readLastDownloadInfo(key) {
  try {
    const raw = localStorage.getItem(`phn_invoice_last_hash_${key}`);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    return {
      changeHash: obj?.changeHash || null,
      count: Number(obj?.count || 0),
      at: Number(obj?.at || 0),
    };
  } catch {
    return null;
  }
}

/** ---------- component ---------- */

export default function SubscriptionInvoicePdfDownLoad({
  data,
  payerEmail,
  brand = {
    name: "Pure Health Nexus",
    url: "https://purehealthnexus.com",
    logo: "/assets/images/logo.png",
  },
  serviceName = "checkout_success_pdf",
}) {
  const invoiceRef = useRef(null);
  const [busyKey, setBusyKey] = useState("");

  const normalized = useMemo(() => {
    const email = data?.payerEmail || payerEmail || "";
    return {
      invoiceId: data?.invoiceId || null,
      subscriptionId: data?.id || "-",
      status: data?.status || "ACTIVE",
      planName: data?.planName || "-",
      currency: data?.currency || "USD",
      price: data?.price ?? 0,
      start: data?.start || null,
      nextBilling: data?.nextBilling || null,
      payerEmail: email,
      payerMasked: maskEmail(email),
    };
  }, [data, payerEmail]);

  // Event envelope for guardrails; treat UI as the origin "source".
  const envelope = useMemo(() => {
    const requestId = mkId("req");
    const traceId = mkId("trace");
    const idempotencyKey = normalized.invoiceId || normalized.subscriptionId || mkId("sub");
    const changeHash = computeChangeHash({ ...normalized });
    return {
      source: "ui",
      requestId,
      traceId,
      visited: ["ui"],
      hopCount: 1,
      publishedAt: new Date().toISOString(),
      idempotencyKey,
      changeHash,
      entityId: normalized.invoiceId || normalized.subscriptionId || null,
      service: serviceName,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    normalized.invoiceId,
    normalized.subscriptionId,
    normalized.price,
    normalized.nextBilling,
    normalized.planName,
    normalized.currency,
    normalized.payerEmail,
  ]);

  const filename = useMemo(() => {
    const base = normalized.invoiceId ? `invoice_${normalized.invoiceId}` : `receipt_${normalized.subscriptionId}`;
    return `${base}_${envelope.requestId}.pdf`;
  }, [normalized.invoiceId, normalized.subscriptionId, envelope.requestId]);

  const idStore = useMemo(() => new IdempotencyStore({ ttlMs: 6 * 60 * 60 * 1000 }), []);
  const publisher = useMemo(
    () =>
      new EventPublisher({
        service: serviceName,
        maxHopCount: 9,
        allowedRoutes: { "ui:click/download": ["worker:generate/pdf"] },
      }),
    [serviceName]
  );

  const handleDownload = async () => {
    if (busyKey === envelope.idempotencyKey) return;
    setBusyKey(envelope.idempotencyKey);

    try {
      // Allow up to MAX_SAME_DOWNLOADS when unchanged.
      const last = readLastDownloadInfo(envelope.idempotencyKey);
      if (last && last.changeHash === envelope.changeHash && last.count >= MAX_SAME_DOWNLOADS) {
        setBusyKey("");
        // eslint-disable-next-line no-alert
        alert(
          `You've already downloaded this ${last.count} time${last.count > 1 ? "s" : ""}. ` +
            `Please wait for an update or save a copy from your downloads.`
        );
        return;
      }

      // Idempotency: block only concurrent work.
      if (!idStore.tryStart(envelope.idempotencyKey, envelope.changeHash)) {
        setBusyKey("");
        return;
      }

      // Publish once (no loops/echoes).
      publisher.publish(envelope, { ingress: "ui:click/download", egress: "worker:generate/pdf" });

      // Work (with retries).
      await withBackoff(async () => {
        const node = invoiceRef.current;
        if (!node) {
          const err = new Error("Invoice DOM not ready.");
          err.permanent = true;
          throw err;
        }

        const scale = computeCaptureScale();
        const canvas = await html2canvas(node, {
          scale,
          useCORS: true,
          backgroundColor: null,
          windowWidth: node.scrollWidth,
          windowHeight: node.scrollHeight,
        });

        const pdfSize = pickPdfSize();
        const pdf = new jsPDF("p", "mm", pdfSize);
        pdf.setProperties({
          title: normalized.invoiceId ? `Invoice ${normalized.invoiceId}` : `Receipt ${normalized.subscriptionId}`,
          subject: `${normalized.planName} • ${formatMoney(normalized.price, normalized.currency)}`,
          author: brand.name,
          keywords: `invoice,subscription,${brand.name}`,
          creator: serviceName,
        });

        await canvasToPdf(pdf, canvas, { marginMm: 10 });
        pdf.save(filename);
      });

      // Mark complete and persist quota state.
      idStore.markComplete(envelope.idempotencyKey, envelope.changeHash);
      persistLastDownloadInfo(envelope.idempotencyKey, envelope.changeHash);
    } catch (e) {
      publisher.dlq.push({ envelope, reason: e?.message || "unknown_error", at: new Date().toISOString() });
      // eslint-disable-next-line no-console
      console.error("Invoice PDF generation failed:", e);
      // eslint-disable-next-line no-alert
      alert("Sorry, the invoice could not be generated. Please try again.");
    } finally {
      setBusyKey("");
    }
  };

  return (
    <>
      {/* Accessible, idempotent trigger button */}
      <button
        type="button"
        className="btn btn-primary"
        aria-label="Download Invoice PDF"
        onClick={handleDownload}
        disabled={!!busyKey}
        title={normalized.invoiceId ? "Download Invoice (PDF)" : "Download Receipt (PDF)"}
      >
        {busyKey ? "Generating…" : "Download Invoice (PDF)"}
      </button>

      {/* Hidden render target */}
      <div className="phn-invoice-hidden-wrapper" aria-hidden="true">
        <div
          ref={invoiceRef}
          className="phn-invoice a4"
          data-source={envelope.source}
          data-service={serviceName}
          data-request-id={envelope.requestId}
          data-trace-id={envelope.traceId}
          data-hop-count={envelope.hopCount}
          data-visited={JSON.stringify(envelope.visited)}
          data-idempotency-key={envelope.idempotencyKey}
          data-change-hash={envelope.changeHash}
          data-published-at={envelope.publishedAt}
        >
          <div className="accent-bar" aria-hidden="true" />

          <header className="inv-header">
            <div className="brand">
              <img src={brand.logo || "/assets/images/logo.png"} className="brand-logo" alt={`${brand.name} logo`} />
              <div className="brand-text">
                <h1 className="brand-name">{brand.name}</h1>
                <p className="brand-url">{brand.url}</p>
              </div>
            </div>
            <div className="inv-meta">
              <p className="inv-title">{normalized.invoiceId ? "Invoice" : "Receipt"}</p>
              <p className="inv-number">
                {normalized.invoiceId ? `# ${normalized.invoiceId}` : `# ${normalized.subscriptionId}`}
              </p>
              <p className="inv-date">Issued: {formatDate(new Date().toISOString())}</p>
            </div>
          </header>

          <section className="inv-billto">
            <div className="col">
              <p className="k">Billed to</p>
              <p className="v">{normalized.payerMasked}</p>
            </div>
            <div className="col">
              <p className="k">Subscription ID</p>
              <p className="v mono">{normalized.subscriptionId}</p>
            </div>
            <div className="col">
              <p className="k">Status</p>
              <p className={`v status ${String(normalized.status || "").toLowerCase()}`}>{normalized.status}</p>
            </div>
          </section>

          <section className="inv-plan">
            <div className="plan-line">
              <div className="plan-left">
                <p className="plan-name" title={normalized.planName}>
                  {normalized.planName}
                </p>

                <div className="plan-meta">
                  <div className="meta-chip start">
                    <span className="meta-dot" aria-hidden="true"></span>
                    <span className="meta-label">Start</span>
                    <span className="meta-value">{formatDate(normalized.start)}</span>
                  </div>
                  <div className="meta-chip next">
                    <span className="meta-dot" aria-hidden="true"></span>
                    <span className="meta-label">Next billing</span>
                    <span className="meta-value">{formatDate(normalized.nextBilling)}</span>
                  </div>
                </div>
              </div>

              <p className="plan-amount">{formatMoney(normalized.price, normalized.currency)}</p>
            </div>
          </section>

          <section className="summary-grid">
            <div className="summary-card total">
              <p className="label">Total Paid</p>
              <p className="value">{formatMoney(normalized.price, normalized.currency)}</p>
            </div>
          </section>

          <section className="inv-notes">
            <p className="muted">
              This document confirms payment collection for your subscription. Keep it for your records.
            </p>
          </section>

          <footer className="inv-footer">
            <p className="fine">© {new Date().getFullYear()} {brand.name}</p>
            <p className="fine-right">Thank you for your business</p>
          </footer>
        </div>
      </div>
    </>
  );
}
